import { useEffect } from "react";

import { Controller, FormProvider, useForm } from "react-hook-form";

import SettingsIcon from "@/assets/icons/cog.svg";
import Button from "@components/Button";
import { Dropdown, DropdownMenu, DropdownToggle } from "@components/Dropdown";

interface FieldOptionsProps {
  options: number[] | string[];
  additionalFormElement?: React.ReactNode;
  show?: boolean;
  onToggle?: (isOpen: boolean) => void;
  value?: string | number;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
}

interface FormData {
  fieldValue: string | number;
}

const FieldOptions: React.FC<FieldOptionsProps> = ({
  options,
  additionalFormElement,
  show,
  onToggle,
  value,
  onChange,
}) => {
  const methods = useForm<FormData>({
    defaultValues: {
      fieldValue: value || options[0] || "",
    },
  });

  const { control, watch, setValue } = methods;
  const watchedValue = watch("fieldValue");

  // Sync external value changes with internal form state
  useEffect(() => {
    if (value !== undefined && value !== watchedValue) {
      setValue("fieldValue", value);
    }
  }, [value, setValue, watchedValue]);

  // Handle form changes and call external onChange
  const handleFieldChange = (newValue: string | number) => {
    // Create a synthetic event to match the expected interface
    const syntheticEvent = {
      target: { value: newValue },
      currentTarget: { value: newValue },
      preventDefault: () => {},
      stopPropagation: () => {},
    } as React.ChangeEvent<HTMLInputElement>;

    onChange(syntheticEvent);
  };

  return (
    <FormProvider {...methods}>
      <div>
        <Dropdown open={show} onOpenChange={onToggle}>
          <DropdownToggle asChild>
            <Button variant="secondary">
              <SettingsIcon height={24} />
            </Button>
          </DropdownToggle>
          <DropdownMenu className="!p-4">
            <div
              className="flex flex-col gap-2"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <label>{L.MENTIONS_QUERYBUILDER_FILTER_MAX} </label>
              <Controller
                name="fieldValue"
                control={control}
                render={({ field }) => (
                  <select
                    {...field}
                    onChange={(e) => {
                      field.onChange(e);
                      handleFieldChange(e.target.value);
                    }}
                  >
                    {options.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                )}
              />
              <label>{L.MENTIONS_QUERYBUILDER_FILTER_WORDS_APART}</label>

              {additionalFormElement && <div>{additionalFormElement}</div>}
            </div>
          </DropdownMenu>
        </Dropdown>
      </div>
    </FormProvider>
  );
};

export default FieldOptions;
